import { ArrowGo, Checked, Plus, Users } from "@assets"
import { Head, Link, router } from "@inertiajs/react"

import { PageHeader } from "@/components/page-header"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { CardTitle } from "@/components/ui/card"
import AppLayout from "@/layouts/app-layout"
import { userPath } from "@/routes"

interface Attendee {
  id: number
  status: string
  user: {
    id: number
    name: string
    avatar_url?: string | null
  }
}

interface Event {
  id: number
  title: string
  start_date?: string
  has_started?: boolean
  neighborhood?: {
    name: string
  }
  organizer: {
    id: number
    name: string
  }
}

interface Props {
  event: Event
  attendees: Attendee[]
  pagination: {
    current_page: number
    total_pages: number
    total_count: number
  }
  can_manage: boolean
}

export default function AttendeesIndex({
  event,
  attendees,
  pagination,
  can_manage,
}: Props) {
  const handleMarkAttended = (attendeeId: number) => {
    router.post(
      `/eventos/${event.id}/asistentes/${attendeeId}/mark_attended`,
      {},
      {
        preserveScroll: true,
      },
    )
  }

  const handleUnmarkAttended = (attendeeId: number) => {
    router.post(
      `/eventos/${event.id}/asistentes/${attendeeId}/unmark_attended`,
      {},
      {
        preserveScroll: true,
      },
    )
  }

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2)
  }

  return (
    <AppLayout
      showEditButton={false}
      showDeleteButton={false}
      showBackButton={false}
      showShareButton={false}
      showFavoriteButton={false}
    >
      <Head title={`Asistentes - ${event.title}`} />

      <div className="mx-auto max-w-4xl space-y-6 p-4">
        {/* Desktop Navigation */}
        <div className="hidden md:block">
          <PageHeader
            showBackButton={false}
            showEdit={false}
            showDelete={false}
            showShare={false}
            showFavorite={false}
          />
        </div>
        <button
          onClick={() => window.history.back()}
          className="w-full hover:underline"
        >
          <div className="flex items-center">
            <ArrowGo className="text-primary size-4 flex-shrink-0 rotate-180 fill-current" />
            <span className="ml-1 flex-1 truncate font-medium">
              {event.title}
            </span>
          </div>
        </button>

        <CardTitle className="text-primary flex items-center justify-center gap-2">
          <Users className="h-5 w-5" />
          {pagination.total_count} asistente
          {pagination.total_count !== 1 ? "s" : ""}
        </CardTitle>
        {attendees.length === 0 ? (
          <div className="text-muted-foreground py-8 text-center">
            No hay asistentes confirmados todavía
          </div>
        ) : (
          <div className="mt-4">
            {attendees.map((attendee) => (
              <div
                key={attendee.id}
                className="border-chart-4 flex flex-wrap items-center justify-between gap-1 border-t py-1"
              >
                <Link
                  href={userPath(attendee.user.id)}
                  className="flex items-center gap-3 transition-opacity hover:opacity-80"
                >
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={attendee.user.avatar_url ?? undefined} />
                    <AvatarFallback className="text-primary gradient-avatar p-1 font-semibold">
                      {getInitials(attendee.user.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{attendee.user.name}</p>
                    <p className="text-muted-foreground text-xs">
                      {attendee.status === "attended"
                        ? "Asistió"
                        : "Confirmado"}
                    </p>
                  </div>
                </Link>

                <div className="flex items-center gap-2">
                  {attendee.status === "attended" && can_manage ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => handleUnmarkAttended(attendee.id)}
                      className="gap-1"
                    >
                      <Plus
                        className="h-3 w-3 fill-white"
                        style={{ transform: "rotate(45deg)" }}
                      />
                      Deshacer
                    </Button>
                  ) : (
                    can_manage &&
                    event.has_started && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleMarkAttended(attendee.id)}
                        className="bg-card gap-1"
                      >
                        <Checked className="fill-primary h-3 w-3" />
                        Check-in
                      </Button>
                    )
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  )
}
